import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';
import {
  contactKeys,
  estimateKeys,
  expenseKeys,
  invoiceKeys,
  membershipKeys,
  pricebookKeys,
  settingsKeys,
} from '@/lib/queryKeys';

// Same shape as useAccountingConflicts — keep in sync. Force-syncing mutates
// the underlying entity (e.g. Contact.displayName, Invoice.balance), so the
// list/detail caches for that entity need to refetch. payment / deposit /
// credit_memo / change_order are nested under contacts.
const ENTITY_KEY_NAMESPACES: Record<string, Array<() => readonly unknown[]>> = {
  contact: [contactKeys.all],
  invoice: [invoiceKeys.all],
  estimate: [estimateKeys.all],
  item: [pricebookKeys.all],
  expense: [expenseKeys.all],
  membership_charge: [membershipKeys.all],
  payment: [contactKeys.all],
  deposit: [contactKeys.all],
  credit_memo: [contactKeys.all],
  change_order: [contactKeys.all],
};

export interface ForceSyncVariables {
  entityType: string;
  entityId: string;
}

export interface ForceSyncResult {
  enqueued: boolean;
  jobId: string;
}

/**
 * Force-sync mutation for a single accounting entity (spec §17.3).
 *
 * Hits `POST /settings/accounting/sync/entity/:type/:id`. Backend enqueues a
 * sync job for the entity regardless of its current sync state — used to
 * retry a synced record (e.g. after correcting upstream data) or push a
 * never-synced record on demand from the Manual Sync UI.
 *
 * Invalidates the per-entity drill-down, the sync history list, and the
 * dashboard totals so the UI reflects the new PENDING state immediately.
 */
export function useAccountingForceSync() {
  const queryClient = useQueryClient();
  return useApiMutation<ForceSyncResult, ForceSyncVariables>({
    // Force Sync explicitly sends bypassConflict:true so the operator's
    // intent ("override and re-sync") is observable in the request payload.
    // Backend defaults to true on this endpoint, but being explicit means a
    // future backend default flip can't silently regress the override.
    mutationFn: ({ entityType, entityId }) =>
      apiPost<ForceSyncResult, { bypassConflict: boolean }>(
        operationsClient,
        settingsEndpoints.accountingSyncForceEntity(entityType, entityId),
        { bypassConflict: true },
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.accountingEntity(vars.entityType, vars.entityId),
      });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.accountingHistory() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.accountingDashboard() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.accountingFailures() });
      // Pull-direction force-syncs mutate the underlying entity (e.g. Contact
      // displayName / address). Invalidate the entity's list/detail caches so
      // pages like /dashboard/contacts reflect the new data without a refresh.
      for (const ns of ENTITY_KEY_NAMESPACES[vars.entityType] ?? []) {
        void queryClient.invalidateQueries({ queryKey: ns() });
      }
      toast.success('Sync queued');
    },
  });
}
